import { ButtonPrimary, ButtonSecondary, Colors, Icon, Pills, Text } from '@amelio/ui-library';

import { InfoTooltip } from './InfoTooltip';
import {
    DRAFT_BANNER,
    DRAFT_BANNER_ACTIONS,
    DRAFT_BANNER_LAYOUT,
    DRAFT_BANNER_STATUS,
} from '../calibrationStyles';

import { useAmelioTranslations } from '@/hooks';

export interface DraftBannerProps {
    /** True as soon as one row of the current cycle carries a draft calibration. */
    hasDraftAdjustments: boolean;
    /** False on a report with no evaluated employee: a sweep would have nothing to touch. */
    hasAdjustableEmployees: boolean;
    onBulkAdjust: () => void;
    onPublish: () => void;
}

/**
 * Opening band of the employee view: where the cycle's calibrations stand, and the two actions
 * that move them along. Neither action is ever a dead button: each is either live,
 * or visibly disabled behind a tooltip that says what is missing.
 */
export const DraftBanner = ({
    hasDraftAdjustments,
    hasAdjustableEmployees,
    onBulkAdjust,
    onPublish,
}: DraftBannerProps) => {
    const { t } = useAmelioTranslations();

    const bulkIcon = (
        <Icon
            name={'lucide-sparkles'}
            color={Colors.PRIMARY}
        />
    );

    const publishIcon = (
        <Icon
            name={'essential-rocket'}
            color={Colors.WHITE}
        />
    );

    return (
        <div
            className={`${DRAFT_BANNER} ${DRAFT_BANNER_LAYOUT}`}
            data-testid={'calibration-draft-banner'}
        >
            <div className={DRAFT_BANNER_STATUS}>
                {hasDraftAdjustments && (
                    <Pills
                        size={'sm'}
                        variant={'lightPurple'}
                        dataTestId={'calibration-draft-banner-pill'}
                    >
                        <Icon
                            name={'edit-edit-2'}
                            size={'xs'}
                            color={Colors.SECONDARY}
                            ariaHidden
                        />

                        {t('calibration.table.status.draft')}
                    </Pills>
                )}

                <Text color={Colors.BASE_500}>
                    {hasDraftAdjustments
                        ? t('calibration.draft.message')
                        : t('calibration.draft.emptyMessage')}
                </Text>
            </div>

            <div className={DRAFT_BANNER_ACTIONS}>
                {hasAdjustableEmployees ? (
                    <ButtonSecondary
                        leftIcon={bulkIcon}
                        onClick={onBulkAdjust}
                        dataTestId={'calibration-bulk-adjust-action'}
                    >
                        {t('calibration.bulk.actionLabel')}
                    </ButtonSecondary>
                ) : (
                    <InfoTooltip
                        message={t('calibration.bulk.disabledTooltip')}
                        dataTestId={'calibration-bulk-adjust-action-disabled'}
                    >
                        <ButtonSecondary
                            disabled
                            leftIcon={bulkIcon}
                        >
                            {t('calibration.bulk.actionLabel')}
                        </ButtonSecondary>
                    </InfoTooltip>
                )}

                {hasDraftAdjustments ? (
                    <ButtonPrimary
                        leftIcon={publishIcon}
                        onClick={onPublish}
                        dataTestId={'calibration-publish-action'}
                    >
                        {t('calibration.publish.actionLabel')}
                    </ButtonPrimary>
                ) : (
                    <InfoTooltip
                        message={t('calibration.publish.disabledTooltip')}
                        dataTestId={'calibration-publish-action-disabled'}
                    >
                        <ButtonPrimary
                            disabled
                            leftIcon={publishIcon}
                        >
                            {t('calibration.publish.actionLabel')}
                        </ButtonPrimary>
                    </InfoTooltip>
                )}
            </div>
        </div>
    );
};
